import type { SupabaseClient } from '@supabase/supabase-js';
import { addHistoryCore } from './history';
import { addContactImageCore } from './images';
import { createTodoCore } from './todos';

export type RelationshipLinkCore = {
    id?: string;
    relatedContactId?: string; // contact.id
    relatedName: string;
    relatedAvatar?: string | null;
    relation: string;
    whereMet?: string;
    whereMetMeta?: any;
    whenMet?: string;
    notes?: string;
};

export type RelationshipRow = {
    id: string;
    contact_id: string;
    related_contact_id: string | null;
    related_name: string;
    related_avatar: string | null;
    relation: string;
    where_met: string | null;
    where_met_meta: any;
    when_met: string | null;
    notes: string | null;
    created_at?: string;
    updated_at?: string;
};

const toLink = (r: RelationshipRow): RelationshipLinkCore => ({
    id: r.id,
    relatedContactId: r.related_contact_id || undefined,
    relatedName: r.related_name,
    relatedAvatar: r.related_avatar,
    relation: r.relation || '',
    whereMet: r.where_met || undefined,
    whereMetMeta: r.where_met_meta || undefined,
    whenMet: r.when_met || undefined,
    notes: r.notes || undefined,
});

const fromLink = (contactId: string, l: RelationshipLinkCore) => ({
    contact_id: contactId,
    related_contact_id: l.relatedContactId ?? null,
    related_name: l.relatedName,
    related_avatar: l.relatedAvatar ?? null,
    relation: l.relation || '',
    where_met: l.whereMet ?? null,
    where_met_meta: l.whereMetMeta ?? {},
    when_met: l.whenMet ?? null,
    notes: l.notes ?? null,
});

function invertRole(role: string): string {
    const r = (role || '').toLowerCase().trim();
    const map: Record<string, string> = {
        wife: 'husband',
        husband: 'wife',
        girlfriend: 'boyfriend',
        boyfriend: 'girlfriend',
        mother: 'father',
        father: 'mother',
        mom: 'dad',
        dad: 'mom',
        daughter: 'parent',
        son: 'parent',
        parent: 'child',
        child: 'parent',
        sister: 'brother',
        brother: 'sister',
        fiance: 'fiancee',
        fiancee: 'fiance',
        mentor: 'mentee',
        mentee: 'mentor',
        manager: 'report',
        boss: 'report',
        report: 'manager',
        coworker: 'coworker',
        friend: 'friend',
        partner: 'partner',
        spouse: 'spouse',
    };
    return map[r] || r;
}

export async function listRelationshipsCore(sb: SupabaseClient, contactId: string): Promise<RelationshipLinkCore[]> {
    const { data, error } = await sb
        .from('contact_relationships')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data as RelationshipRow[] | null)?.map(toLink) || [];
}

export async function createRelationshipCore(sb: SupabaseClient, contactId: string, link: RelationshipLinkCore): Promise<RelationshipLinkCore> {
    const payload = fromLink(contactId, link);
    const { data, error } = await sb
        .from('contact_relationships')
        .insert(payload)
        .select('*')
        .single();
    if (error) throw error;
    const saved = toLink(data as RelationshipRow);

    try {
        if (saved.relatedAvatar) {
            await addContactImageCore(sb, contactId, saved.relatedAvatar, { source_type: 'relationship', source_id: saved.id });
        }
    } catch (e) {
        console.warn('Failed to add relationship avatar to gallery', e);
    }

    if (link.relatedContactId) {
        try {
            const { data: me, error: meErr } = await sb
                .from('contacts')
                .select('first_name,last_name,profile_image_url,photo')
                .eq('id', contactId)
                .maybeSingle();
            if (meErr) throw meErr;
            const myName = `${(me?.first_name || '').trim()} ${(me?.last_name || '').trim()}`.trim() || 'Unknown';
            const myAvatar = (me?.profile_image_url || me?.photo || null) as string | null;

            const inverseRole = invertRole(link.relation || '');
            const reversePayload = fromLink(link.relatedContactId, {
                relatedContactId: contactId,
                relatedName: myName,
                relatedAvatar: myAvatar,
                relation: inverseRole,
                whereMet: link.whereMet,
                whereMetMeta: link.whereMetMeta,
                whenMet: link.whenMet,
                notes: link.notes,
            } as RelationshipLinkCore);

            const { data: existing, error: existErr } = await sb
                .from('contact_relationships')
                .select('id')
                .eq('contact_id', link.relatedContactId)
                .eq('related_contact_id', contactId)
                .maybeSingle();
            if (existErr && (existErr as any).code !== 'PGRST116') {
                throw existErr;
            }
            if (!existing) {
                await sb.from('contact_relationships').insert(reversePayload);
                try {
                    await addHistoryCore(sb, {
                        contact_id: link.relatedContactId,
                        action: 'relationship_linked',
                        details: { name: myName, relatedContactId: contactId, relation: inverseRole || '' },
                    });
                } catch { }
            }
        } catch (e) {
            console.error('Failed to create reciprocal relationship', e);
        }
    }

    await autoCreateMeetingEventCore(sb, contactId, saved, link.relatedContactId ? 'contact' : 'person');

    return saved;
}

export async function updateRelationshipCore(sb: SupabaseClient, id: string, link: RelationshipLinkCore): Promise<RelationshipLinkCore> {
    const { data, error } = await sb
        .from('contact_relationships')
        .update({
            related_contact_id: link.relatedContactId ?? null,
            related_name: link.relatedName,
            related_avatar: link.relatedAvatar ?? null,
            relation: link.relation || '',
            where_met: link.whereMet ?? null,
            where_met_meta: link.whereMetMeta ?? {},
            when_met: link.whenMet ?? null,
            notes: link.notes ?? null,
        })
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw error;
    const updated = toLink(data as RelationshipRow);
    try {
        if (updated.relatedAvatar) {
            const { data: row } = await sb
                .from('contact_relationships')
                .select('contact_id')
                .eq('id', id)
                .maybeSingle();
            const ownerId = (row as any)?.contact_id as string | undefined;
            if (ownerId) {
                await addContactImageCore(sb, ownerId, updated.relatedAvatar, { source_type: 'relationship', source_id: id });
            }
        }
    } catch (e) {
        console.warn('Failed to sync relationship avatar to gallery', e);
    }

    if (link.relatedContactId) {
        try {
            const { data: orig, error: origErr } = await sb
                .from('contact_relationships')
                .select('contact_id')
                .eq('id', id)
                .maybeSingle();
            if (origErr) throw origErr;
            const contactId = (orig as any)?.contact_id as string | undefined;
            if (contactId) {
                const { data: me, error: meErr } = await sb
                    .from('contacts')
                    .select('first_name,last_name,profile_image_url,photo')
                    .eq('id', contactId)
                    .maybeSingle();
                if (meErr) throw meErr;
                const myName = `${(me?.first_name || '').trim()} ${(me?.last_name || '').trim()}`.trim() || 'Unknown';
                const myAvatar = (me?.profile_image_url || me?.photo || null) as string | null;

                const { data: reverseRow } = await sb
                    .from('contact_relationships')
                    .select('*')
                    .eq('contact_id', link.relatedContactId)
                    .eq('related_contact_id', contactId)
                    .maybeSingle();

                if (reverseRow) {
                    await sb
                        .from('contact_relationships')
                        .update({
                            related_name: myName,
                            related_avatar: myAvatar,
                            relation: invertRole(link.relation || ''),
                            where_met: link.whereMet ?? null,
                            where_met_meta: link.whereMetMeta ?? {},
                            when_met: link.whenMet ?? null,
                            notes: link.notes ?? null,
                        })
                        .eq('id', (reverseRow as any).id);
                } else {
                    await sb.from('contact_relationships').insert({
                        contact_id: link.relatedContactId,
                        related_contact_id: contactId,
                        related_name: myName,
                        related_avatar: myAvatar,
                        relation: invertRole(link.relation || ''),
                        where_met: link.whereMet ?? null,
                        where_met_meta: link.whereMetMeta ?? {},
                        when_met: link.whenMet ?? null,
                        notes: link.notes ?? null,
                    });
                }
            }
        } catch (e) {
            console.error('Failed to upsert reciprocal relationship on update', e);
        }
    }

    return updated;
}

export async function deleteRelationshipCore(sb: SupabaseClient, id: string): Promise<void> {
    // Fetch original to compute reciprocal
    const { data: orig, error: selErr } = await sb
        .from('contact_relationships')
        .select('id, contact_id, related_contact_id')
        .eq('id', id)
        .maybeSingle();
    if (selErr) throw selErr;

    const { error } = await sb
        .from('contact_relationships')
        .delete()
        .eq('id', id);
    if (error) throw error;

    const contactId = (orig as any)?.contact_id as string | undefined;
    const relatedId = (orig as any)?.related_contact_id as string | undefined;
    if (contactId && relatedId) {
        // Attempt to delete reciprocal link; ignore not-found errors
        try {
            await sb
                .from('contact_relationships')
                .delete()
                .eq('contact_id', relatedId)
                .eq('related_contact_id', contactId);
        } catch (e) {
            // Surface in dev logs but do not mask primary deletion success
            console.warn('Failed to delete reciprocal relationship', e);
        }
    }
}

async function autoCreateMeetingEventCore(
    sb: SupabaseClient,
    contactId: string,
    relationship: RelationshipLinkCore,
    contactType: 'contact' | 'person'
) {
    if (!relationship.whereMet && !relationship.whenMet) return;

    try {
        const { data: contact, error: contactErr } = await sb
            .from('contacts')
            .select('user_id, first_name, last_name')
            .eq('id', contactId)
            .single();

        if (contactErr) throw contactErr;

        const personName = relationship.relatedName;
        const location = relationship.whereMet;
        const title = location ? `Met ${personName} at ${location}` : `Met ${personName}`;

        let meetingDate = new Date().toISOString();
        if (relationship.whenMet) {
            try {
                const parsed = new Date(relationship.whenMet);
                if (!isNaN(parsed.getTime())) {
                    if (relationship.whenMet.includes('T00:00:00')) {
                        parsed.setHours(14, 0, 0, 0);
                    }
                    meetingDate = parsed.toISOString();
                }
            } catch { }
        }

        await createTodoCore(sb, {
            title,
            description: `Meeting automatically created from relationship.\n\nRelation: ${relationship.relation}\nNotes: ${relationship.notes || 'No additional notes.'}`,
            location: relationship.whereMet || null,
            due_date: meetingDate,
            done: false,
            contact_id: relationship.relatedContactId || null,
            assigned_contact_id: contactId,
            group_id: null,
            event_id: null,
            recurrence: null,
            reminder_minutes: null,
        }, (contact as any).user_id as string);

        console.log(`Auto-created calendar event: "${title}" for contact relationship`);
    } catch (error) {
        console.error('Failed to auto-create meeting event:', error);
    }
}
